import { useRef, useState } from 'react';
import { Check, Archive, AlertCircle, Users, Clock, CircleCheck, MessageSquareText } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import type { Asunto } from '@/api/asuntos.api';
import { CAJON_COLOR, PRIORIDAD_TONO, PRIORIDAD_NOMBRE, hora12, fechaCorta, plata } from '@/lib/athena';

/**
 * UNA TARJETA DE ASUNTO, con gestos.
 *
 * Mandato: deslizar a la derecha aprueba (verde), a la izquierda descarta
 * (gris). Se usa igual en Hoy, en Me toca y en la ficha de una persona: una
 * sola tarjeta, o las tres pantallas se separan solas.
 *
 * ═══ POR QUÉ EL GESTO SE IMPLEMENTA A MANO ═══
 *
 * Con eventos de puntero, sin librería. Una librería de gestos para esto son
 * 40 KB que el teléfono descarga para resolver un arrastre horizontal.
 *
 * Tres detalles que hacen que se sienta bien y que son la diferencia entre un
 * gesto y un accidente:
 *
 *  1. **Umbral de 72 px.** Menos que eso y un scroll medio torcido dispararía
 *     la acción. La tarjeta vuelve sola a su sitio si no se llega.
 *  2. **Se decide el eje una sola vez.** Si el primer movimiento es más
 *     vertical que horizontal, es scroll y la tarjeta no se mueve más. Sin
 *     esto, la lista se traba al intentar bajar.
 *  3. **El fondo aparece mientras se arrastra**, así se ve qué va a pasar
 *     antes de soltar. Un gesto sin respuesta previa es una apuesta.
 */

interface Props {
  asunto: Asunto;
  onAbrir: (id: string) => void;
  onAprobar?: (id: string) => void;
  onDescartar?: (id: string) => void;
  /** Mantener presionado / clic derecho. Sin esto, el menú no aparece. */
  onPosponer?: (id: string, hasta: string) => void;
  onVerMensaje?: (a: Asunto) => void;
  /** `false` en listas donde deslizar no aplica (ya cerradas). */
  conGestos?: boolean;
  /** Muestra la fecha completa en vez de solo la hora. */
  conFecha?: boolean;
}

const UMBRAL = 72;

export function TarjetaDeAsunto({
  asunto: a,
  onAbrir,
  onAprobar,
  onDescartar,
  onPosponer,
  onVerMensaje,
  conGestos = true,
  conFecha = false,
}: Props) {
  const [dx, setDx] = useState(0);
  const [saliendo, setSaliendo] = useState<'izq' | 'der' | null>(null);
  const [menu, setMenu] = useState(false);
  const inicio = useRef<{ x: number; y: number } | null>(null);
  const eje = useRef<'indeciso' | 'x' | 'y'>('indeciso');
  const presion = useRef<ReturnType<typeof setTimeout> | null>(null);

  // MANTENER PRESIONADO = menú rápido. 450 ms: menos que eso y un toque normal
  // lo dispara; más, y la gente suelta antes de que pase algo.
  const cancelarPresion = () => {
    if (presion.current) clearTimeout(presion.current);
    presion.current = null;
  };

  const color = CAJON_COLOR[a.cajon];

  const alBajar = (e: React.PointerEvent) => {
    if (!conGestos) return;
    inicio.current = { x: e.clientX, y: e.clientY };
    eje.current = 'indeciso';
    cancelarPresion();
    presion.current = setTimeout(() => {
      // El menú cancela el arrastre: si no, al soltar se dispararía además la
      // acción del deslizamiento y el dueño vería dos cosas por un gesto.
      inicio.current = null;
      setDx(0);
      setMenu(true);
    }, 450);
  };

  const alMover = (e: React.PointerEvent) => {
    if (!inicio.current) return;
    const dX = e.clientX - inicio.current.x;
    const dY = e.clientY - inicio.current.y;

    // El eje se decide UNA vez, con el primer movimiento claro. Si no, la
    // lista se traba cada vez que alguien baja con el dedo medio torcido.
    if (eje.current === 'indeciso') {
      if (Math.abs(dX) < 8 && Math.abs(dY) < 8) return;
      eje.current = Math.abs(dX) > Math.abs(dY) ? 'x' : 'y';
    }
    if (eje.current === 'y') return;
    // Moverse cancela el "mantener": arrastrar y mantener son gestos distintos.
    cancelarPresion();
    setDx(dX);
  };

  const alSoltar = () => {
    cancelarPresion();
    if (!inicio.current) return;
    const movido = dx;
    inicio.current = null;
    eje.current = 'indeciso';

    if (movido > UMBRAL && onAprobar) {
      setSaliendo('der');
      // Se deja ver la animación antes de avisar al padre: si la fila
      // desaparece de golpe, no se entiende qué pasó.
      setTimeout(() => onAprobar(a.id), 180);
      return;
    }
    if (movido < -UMBRAL && onDescartar) {
      setSaliendo('izq');
      setTimeout(() => onDescartar(a.id), 180);
      return;
    }
    setDx(0);
  };

  const monto = plata(a.montoCentavos, a.moneda);

  return (
    <div className="relative overflow-hidden">
      {/* El fondo dice qué va a pasar, ANTES de soltar. */}
      {conGestos && dx !== 0 && (
        <div
          className={cn(
            'absolute inset-0 flex items-center px-5 text-sm font-medium',
            dx > 0
              ? 'justify-start bg-emerald-500 text-white'
              : 'justify-end bg-slate-400 text-white',
          )}
        >
          {dx > 0 ? (
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4" /> {a.esSuposicion ? 'Aprobar' : 'Hecho'}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Archive className="h-4 w-4" /> Descartar
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => onAbrir(a.id)}
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alSoltar}
        onContextMenu={(e) => {
          if (!conGestos) return;
          e.preventDefault();
          setMenu(true);
        }}
        style={{
          transform: saliendo
            ? `translateX(${saliendo === 'der' ? 400 : -400}px)`
            : `translateX(${dx}px)`,
          opacity: saliendo ? 0 : 1,
          touchAction: 'pan-y',
        }}
        className={cn(
          'relative flex w-full gap-3 border-l-4 bg-white px-3 py-3 text-left transition-[transform,opacity] duration-150 dark:bg-slate-900',
          'border-b border-b-slate-100 last:border-b-0 dark:border-b-slate-800',
          // 44 px mínimos de zona de toque, como pide el mandato.
          'min-h-[64px] active:bg-slate-50 dark:active:bg-slate-800/60',
          color.borde,
          a.esSuposicion && 'border-dashed',
        )}
      >
        <span className="w-[68px] shrink-0 pt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {conFecha ? fechaCorta(a.fechaCompromiso) : hora12(a.fechaCompromiso)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-slate-900 dark:text-slate-100">
            {a.descripcion}
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
            {a.conQuien}
            {a.tema ? ` · ${a.tema}` : ''}
            {monto ? ` · ${monto}` : ''}
          </span>

          <span className="mt-1 flex flex-wrap items-center gap-1">
            {a.vencido && (
              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
                Vencido
              </Badge>
            )}
            {a.prioridad === 'ALTA' && !a.vencido && (
              <Badge className={PRIORIDAD_TONO.ALTA}>{PRIORIDAD_NOMBRE.ALTA}</Badge>
            )}
            {a.confianza === 'DUDOSO' && (
              <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-300">
                <AlertCircle className="h-3 w-3" /> Dudoso
              </span>
            )}
            {a.vistoEnChats > 1 && (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Users className="h-3 w-3" /> {a.vistoEnChats} chats
              </span>
            )}
            {a.responsable && (
              <span className="text-xs text-slate-400">→ {a.responsable.nombre}</span>
            )}
          </span>
        </span>
      </button>

      {/* EL MENÚ RÁPIDO. Mantener presionado en móvil, clic derecho en
          escritorio. Lo que hay acá es lo que se hace sin abrir la hoja:
          posponer con tres plazos y saltar al mensaje. */}
      {menu && (
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/40">
          {onAprobar && (
            <button
              onClick={() => {
                setMenu(false);
                onAprobar(a.id);
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
            >
              {a.esSuposicion ? <Check className="h-3.5 w-3.5" /> : <CircleCheck className="h-3.5 w-3.5" />}
              {a.esSuposicion ? 'Aprobar' : 'Hecho'}
            </button>
          )}
          {onPosponer &&
            [
              { et: 'Esta tarde', h: 6 },
              { et: 'Mañana', h: 24 },
              { et: 'El lunes', h: 72 },
            ].map((o) => (
              <button
                key={o.et}
                onClick={() => {
                  setMenu(false);
                  onPosponer(a.id, new Date(Date.now() + o.h * 3_600_000).toISOString());
                }}
                className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
              >
                <Clock className="h-3.5 w-3.5" /> {o.et}
              </button>
            ))}
          {onVerMensaje && (
            <button
              onClick={() => {
                setMenu(false);
                onVerMensaje(a);
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs dark:bg-slate-900"
            >
              <MessageSquareText className="h-3.5 w-3.5" /> Ver mensaje
            </button>
          )}
          {onDescartar && (
            <button
              onClick={() => {
                setMenu(false);
                onDescartar(a.id);
              }}
              className="flex min-h-[36px] items-center gap-1 rounded-md bg-white px-2.5 text-xs text-slate-500 dark:bg-slate-900"
            >
              <Archive className="h-3.5 w-3.5" /> Descartar
            </button>
          )}
          <button
            onClick={() => setMenu(false)}
            className="min-h-[36px] px-2 text-xs text-slate-400"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
